/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ServiceManager } from '../service-manager';

export class EventGenerator {
    attrs: string[];

    constructor(
        readonly name: string,
        readonly module: any,
        readonly service: ServiceManager,
        // 前缀公共事件配置，用于生成公共事件
        readonly attrsPrefixEventMap: Record<string, { eventName: string; fetchEventArgs: () => any[] }> = {},
    ) {
        // 获取类的原型，用于过滤原型链上的属性
        const prototype = Object.getPrototypeOf(module);

        // 过滤出实例属性：own property + 不是函数 + 不在原型链上 + 不是特殊属性
        const attrs = Object.keys(module).filter((key) => {
            // 1. 必须是 own property
            if (!Object.prototype.hasOwnProperty.call(module, key)) {
                return false;
            }
            // 2. 不是函数
            if (typeof module[key] === 'function') {
                return false;
            }
            // 3. 不在原型链上（排除继承的属性）
            if (prototype && Object.prototype.hasOwnProperty.call(prototype, key)) {
                return false;
            }
            // 4. 排除特殊属性
            const excludeKeys = ['service', 'identity'];
            if (excludeKeys.includes(key) || key.startsWith('_')) {
                return false;
            }

            return true;
        });

        this.attrs = attrs;

        attrs.forEach((attr) => {
            const eventName = this.__getEventName(name, attr);
            const attrName = this.__capitalize(attr);
            const commonEvent = this.__getCommonEvent(attr, attrsPrefixEventMap);
            const commonEventName = this.__getEventName(name, commonEvent?.eventName);
            module[`get${attrName}`] = () => {
                return module[attr];
            };
            module[`set${attrName}`] = (value: any) => {
                if (module[attr] === value) return;
                module[attr] = value;
                service.events.emit(eventName, value);
                if (commonEvent) {
                    service.events.emit(commonEventName, ...commonEvent.fetchEventArgs());
                }
            };
            module[`on${attrName}Change`] = (fn: (value: any) => void, widget?: number) => {
                return service.events.on(eventName, fn, widget);
            };
            if (commonEvent) {
                module[commonEvent.eventName] = (fn: (value: any) => void, widget?: number) => {
                    return service.events.on(commonEventName, fn, widget);
                };
            }
        });
    }

    private __getCommonEvent(
        attr: string,
        attrsPrefixEventMap: Record<string, { eventName: string; fetchEventArgs: () => any[] }>,
    ) {
        const prefix = Object.keys(attrsPrefixEventMap).find((prefix) => attr.startsWith(prefix));
        if (!prefix) return;
        return attrsPrefixEventMap[prefix];
    }

    private __getEventName = (name: string, attr?: string) => `${name}:${attr ?? ''}`;

    private __capitalize = (str: string) => str[0].toUpperCase() + str.slice(1);
}
